import React, { useEffect, useState } from "react";

import { PanelWithBreadCrumbAndSearchBox } from "../panels/PanelWithBreadCrumbAndSearchBox";
import { PanelWithHeader } from "../panels/PanelWithHeader";
import {
  ImportantDateAddOrUpdatePopup,
  IMPORTANT_DATE_TYPES,
} from "../importantDates/ImportantDateAddOrUpdatePopup";
import {
  apiGetImportantDates,
  apiAddOrUpdateImportantDate,
  apiDeleteImportantDate,
} from "../../services/ImportantDatesService";
import "../importantDates/ImportantDatesPage.css";

type ImportantDatesPageProps = {
  value: string;
};

const TYPE_META: Record<string, { label: string; icon: string }> = {
  svatek: { label: "Svátek", icon: "fas fa-birthday-cake" },
  narozeniny: { label: "Narozeniny", icon: "fas fa-gift" },
  vyroci: { label: "Výročí", icon: "fas fa-heart" },
};

export const ImportantDatesPage = (props: ImportantDatesPageProps) => {
  const [rows, setRows] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<string>("all");
  const [popup, setPopup] = useState<{ visible: boolean; item: any }>({
    visible: false,
    item: undefined,
  });

  useEffect(() => {
    ensureLatestData();
  }, []);

  const ensureLatestData = () => {
    apiGetImportantDates().then((response) => {
      const data = Array.isArray(response.data) ? response.data : [];
      setRows(data);
    });
  };

  const handleAdd = () => setPopup({ visible: true, item: undefined });
  const handleEdit = (item: any) => setPopup({ visible: true, item });
  const handleClose = () => setPopup({ visible: false, item: undefined });

  const handleSave = (formData: any) => {
    apiAddOrUpdateImportantDate(formData).then(() => {
      handleClose();
      ensureLatestData();
    });
  };

  const handleDelete = (id: any) => {
    if (!window.confirm("Opravdu smazat tuto událost?")) {
      return;
    }
    apiDeleteImportantDate(id).then(() => {
      ensureLatestData();
    });
  };

  const formatDate = (value: any) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("cs-CZ");
  };

  const formatDayMonth = (value: any) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    return `${date.getDate()}. ${date.getMonth() + 1}.`;
  };

  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  // These events recur every year -> compute the next occurrence + days remaining
  const nextOccurrenceInfo = (value: any) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return { days: null as number | null, year: null as number | null };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let next = new Date(today.getFullYear(), date.getMonth(), date.getDate());
    if (next < today) {
      next = new Date(today.getFullYear() + 1, date.getMonth(), date.getDate());
    }
    const days = Math.round((next.getTime() - today.getTime()) / MS_PER_DAY);
    return { days, year: date.getFullYear() };
  };

  const daysLabel = (days: number | null) => {
    if (days === null) return "";
    if (days === 0) return "Dnes";
    if (days === 1) return "Zítra";
    return `za ${days} dní`;
  };

  const filteredRows = rows.filter(
    (row: any) => selectedType === "all" || row.type === selectedType
  );

  const sortedRows = [...filteredRows].sort((a: any, b: any) => {
    const da = nextOccurrenceInfo(a.date).days;
    const db = nextOccurrenceInfo(b.date).days;
    return (da === null ? 99999 : da) - (db === null ? 99999 : db);
  });

  const countByType = (type: string) =>
    rows.filter((row: any) => row.type === type).length;

  const fullName = (row: any) =>
    [row.firstName, row.lastName].filter((part: any) => part).join(" ");

  return (
    <div className="container-fluid mt-3">
      {popup.visible && (
        <ImportantDateAddOrUpdatePopup
          handleClose={handleClose}
          handleConfirmation={handleSave}
          item={popup.item}
        />
      )}

      <PanelWithBreadCrumbAndSearchBox currentPageName="Významné dny" />

      <div className="row wow fadeIn">
        <div className="col-lg-12 mb-4">
          <PanelWithHeader
            header="Významné dny / Svátky, narozeniny a výročí"
            iconClassName="far fa-calendar-check"
            showAddIcon={true}
            handleAddAction={handleAdd}
          >
            {/* Type filter chips */}
            <div className="idFilter">
              <button
                className={
                  "idChip" + (selectedType === "all" ? " active" : "")
                }
                onClick={() => setSelectedType("all")}
              >
                Vše ({rows.length})
              </button>
              {IMPORTANT_DATE_TYPES.map((type) => (
                <button
                  key={type.value}
                  className={
                    "idChip" + (selectedType === type.value ? " active" : "")
                  }
                  onClick={() => setSelectedType(type.value)}
                >
                  <i className={type.icon}></i> {type.label} (
                  {countByType(type.value)})
                </button>
              ))}
            </div>

            <div className="table-responsive">
              <table className="idTable">
                <thead>
                  <tr>
                    <th className="idColSm">Datum</th>
                    <th className="idColSm">Typ</th>
                    <th>Osoba</th>
                    <th>Název události</th>
                    <th className="idColSm">Blíží se</th>
                    <th>Popis</th>
                    <th className="text-right">Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="idTable__empty">
                        <i className="far fa-calendar-times"></i>
                        <div>Žádné události</div>
                      </td>
                    </tr>
                  )}
                  {sortedRows.map((row: any) => {
                    const meta = TYPE_META[row.type] || {
                      label: row.type,
                      icon: "far fa-calendar",
                    };
                    const info = nextOccurrenceInfo(row.date);
                    return (
                      <tr key={row.importantDateId}>
                        <td className="idColSm idDateCell">
                          <span className="idDateCell__day">
                            {formatDayMonth(row.date)}
                          </span>
                          <span className="idDateCell__year">
                            {formatDate(row.date)}
                          </span>
                        </td>
                        <td className="idColSm">
                          <span className={"idBadge " + row.type}>
                            <i className={meta.icon}></i> {meta.label}
                          </span>
                        </td>
                        <td className="idPerson">{fullName(row) || "—"}</td>
                        <td>{row.title || "—"}</td>
                        <td className="idColSm">
                          <span
                            className={
                              "idSoon" +
                              (info.days !== null && info.days <= 7
                                ? " near"
                                : "")
                            }
                          >
                            {daysLabel(info.days)}
                          </span>
                        </td>
                        <td className="idDescCell">{row.description}</td>
                        <td className="text-right">
                          <button
                            className="idAction edit"
                            title="Upravit"
                            onClick={() => handleEdit(row)}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            className="idAction delete"
                            title="Smazat"
                            onClick={() => handleDelete(row.importantDateId)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </PanelWithHeader>
        </div>
      </div>
    </div>
  );
};
